import React, { useEffect, useState, useCallback } from "react";
import { Lightbulb, BookOpenCheck, Volume2 } from "lucide-react";

interface ToolbarAction {
  id: string;
  label: string;
  iconClassName: string;
  renderIcon: (className: string) => React.ReactNode;
}

interface SelectedTextFloatingToolbarProps {
  containerRef: React.RefObject<HTMLDivElement>;
  onAction?: (actionId: string, selectedText: string) => void;
}

const FlashcardsIcon = ({ className }: { className?: string }) => (
  <svg
    stroke="currentColor"
    fill="currentColor"
    strokeWidth="0"
    viewBox="0 0 256 256"
    className={className}
    height="1em"
    width="1em"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M180,72H36A20,20,0,0,0,16,92V204a20,20,0,0,0,20,20H180a20,20,0,0,0,20-20V92A20,20,0,0,0,180,72Zm-4,128H40V96H176ZM240,52V176a12,12,0,0,1-24,0V56H64a12,12,0,0,1,0-24H220A20,20,0,0,1,240,52Z" />
  </svg>
);

const AddToNotesIcon = ({ className }: { className?: string }) => (
  <svg
    stroke="currentColor"
    fill="currentColor"
    strokeWidth="0"
    viewBox="0 0 256 256"
    className={className}
    height="1em"
    width="1em"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M229.66,58.34l-32-32a8,8,0,0,0-11.32,0l-96,96A8,8,0,0,0,88,128v32a8,8,0,0,0,8,8h32a8,8,0,0,0,5.66-2.34l96-96A8,8,0,0,0,229.66,58.34ZM124.69,152H104V131.31l64-64L188.69,88ZM200,76.69,179.31,56,192,43.31,212.69,64ZM224,128v80a16,16,0,0,1-16,16H48a16,16,0,0,1-16-16V48A16,16,0,0,1,48,32h80a8,8,0,0,1,0,16H48V208H208V128a8,8,0,0,1,16,0Z" />
  </svg>
);

const ChatIcon = ({ className }: { className?: string }) => (
  <svg
    stroke="currentColor"
    fill="currentColor"
    strokeWidth="0"
    viewBox="0 0 256 256"
    className={className}
    height="1em"
    width="1em"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M128,20A108,108,0,0,0,31.85,177.23L21,209.66A20,20,0,0,0,46.34,235l32.43-10.81A108,108,0,1,0,128,20Zm0,192a84,84,0,0,1-42.06-11.27,12,12,0,0,0-6-1.62,12.1,12.1,0,0,0-3.8.62l-29.79,9.93,9.93-29.79a12,12,0,0,0-1-9.81A84,84,0,1,1,128,212Z" />
  </svg>
);

const toolbarActions: ToolbarAction[] = [
  {
    id: "explain",
    label: "Explain",
    iconClassName: "h-3.5 w-3.5 group-hover/button:text-premium",
    renderIcon: (className) => <Lightbulb className={className} />,
  },
  {
    id: "chat",
    label: "Chat",
    iconClassName: "h-3.5 w-3.5 group-hover/button:text-teal",
    renderIcon: (className) => <ChatIcon className={className} />,
  },
  {
    id: "quiz",
    label: "Quiz",
    iconClassName: "h-3.5 w-3.5 group-hover/button:text-incorrect",
    renderIcon: (className) => <BookOpenCheck className={className} />,
  },
  {
    id: "flashcards",
    label: "Flashcards",
    iconClassName: "h-3.5 w-3.5 group-hover/button:text-warning",
    renderIcon: (className) => <FlashcardsIcon className={className} />,
  },
  {
    id: "add-to-notes",
    label: "Add to notes",
    iconClassName: "h-3.5 w-3.5 group-hover/button:text-info",
    renderIcon: (className) => <AddToNotesIcon className={className} />,
  },
  {
    id: "read-aloud",
    label: "Read aloud",
    iconClassName: "h-3.5 w-3.5 group-hover/button:text-correct",
    renderIcon: (className) => <Volume2 className={className} />,
  },
];

export const SelectedTextFloatingToolbar: React.FC<SelectedTextFloatingToolbarProps> = ({
  containerRef,
  onAction,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [selectedText, setSelectedText] = useState("");

  // Only show toolbar when mouse is released (selection complete)
  const handleMouseUp = useCallback(() => {
    // Small delay to let the browser finalize the selection
    requestAnimationFrame(() => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed || !containerRef.current) {
        return;
      }

      const text = selection.toString().trim();
      if (!text) return;

      const range = selection.getRangeAt(0);
      if (!containerRef.current.contains(range.commonAncestorContainer)) return;

      // Get the last rect of the range to position at selection end
      const rects = range.getClientRects();
      const lastRect = rects[rects.length - 1];
      if (!lastRect) return;

      const toolbarWidth = 580;
      let left = lastRect.right - toolbarWidth / 2;

      const padding = 16;
      if (left < padding) left = padding;
      if (left + toolbarWidth > window.innerWidth - padding) {
        left = window.innerWidth - toolbarWidth - padding;
      }

      setPosition({
        top: lastRect.bottom + 8, // Position below the end of selection
        left,
      });
      setSelectedText(text);
      setIsVisible(true);
    });
  }, [containerRef]);

  // Hide toolbar when selection is cleared (e.g. clicking away)
  const handleSelectionChange = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed) {
      setIsVisible(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("selectionchange", handleSelectionChange);
    
    return () => {
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("selectionchange", handleSelectionChange);
    };
  }, [handleMouseUp, handleSelectionChange]);

  const handleAction = (actionId: string) => {
    if (onAction && selectedText) {
      onAction(actionId, selectedText);
    }
    // Clear selection after action
    window.getSelection()?.removeAllRanges();
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div
      className="fixed z-[9999] mt-1.5 flex items-center justify-between rounded-lg border border-border bg-background p-1 shadow-md dark:border-primary/10 dark:bg-card"
      style={{ top: position.top, left: position.left }}
      onMouseDown={(e) => e.preventDefault()} // Prevent losing selection when clicking toolbar
    >
      <div className="flex items-center">
        {toolbarActions.map((action, index) => (
          <div key={action.id} className="flex items-center">
            {index > 0 && (
              <div className="mx-1 h-6 w-px bg-border dark:bg-primary/10" />
            )}
            <button
              className="group/button flex h-9 w-full items-center justify-start gap-1.5 whitespace-nowrap rounded-md px-2 py-0.5 text-sm font-medium ring-offset-background transition-colors hover:bg-primary/5 hover:text-accent-foreground focus-visible:outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-primary/10"
              aria-label={action.label}
              onClick={() => handleAction(action.id)}
            >
              <div className="flex-shrink-0">
                {action.renderIcon(action.iconClassName)}
              </div>
              <span className="text-primary/80 group-hover/button:text-primary dark:text-primary/80 dark:group-hover/button:text-primary/100">
                {action.label}
              </span>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
